import { change } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'app/store/rootReducer';
import {
  selectQuestion,
  getVisibleDictionary,
  FormsConfigActions,
} from 'app/store/data/formsConfig';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { getParamValues } from 'app/store/data/form';
import { Dictionary, DictionaryEntryKey } from 'app/services/dict.service';
import { QuotationService } from 'app/services/quotation.service';
import { getQuotationId } from 'app/store/data/quotation';
import { runBusinessFunction } from 'app/services/businessFunctions.service';
import { JsonPatchValueType } from 'app/shared/types/JsonPatchValue.type';

export function useField(formName: FormsEnum, questionKey: FieldsEnum) {
  const dispatch = useDispatch();

  const question = useSelector((state: RootState) =>
    selectQuestion({ state, formName, questionKey })
  );

  const dictionary = useSelector((state: RootState) => {
    return getVisibleDictionary(
      state,
      formName,
      `${questionKey}_dictionary`,
      question.answersDictionaryKey
    );
  });

  const paramValues = JSON.parse(
    useSelector((state: RootState) =>
      getParamValues(state, formName, question.answersDictionaryParams || [])
    )
  );

  const setDictionary = (dictionary: Dictionary) => {
    return dispatch(
      FormsConfigActions.setDictionaryAction({
        dictionaryKey: `${questionKey}_dictionary`,
        dictionary,
        formName,
      })
    );
  };

  const changeValue = (value: string | number | DictionaryEntryKey) =>
    dispatch(change(formName, questionKey, value));

  const runBusinessFunctions = () => {
    if (question.onChange) {
      question.onChange.forEach((functionName: string) =>
        runBusinessFunction(functionName, false)
      );
    }
  };

  const quotationId = useSelector(getQuotationId);

  const patchQuotation = (value: JsonPatchValueType) => {
    if (question.shouldPatch === false || value === undefined) {
      return;
    }

    QuotationService.update(quotationId, question.modelPath, value);
  };

  const clearDictionaries = (questionKeys: Array<FieldsEnum>) => {
    dispatch(
      FormsConfigActions.setDictionariesAction({
        formName,
        dictionaries: questionKeys.map((key) => ({
          dictionaryKey: `${key}_dictionary`,
          dictionary: [],
        })),
      })
    );
  };

  return {
    question,
    dictionary,
    paramValues,
    setDictionary,
    patchQuotation,
    changeValue,
    runBusinessFunctions,
    clearDictionaries,
  };
}
